import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CalendarCheck } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EinsatzplanBestaetigen() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/einsatzplaene/by-token/${token}`);
        if (!mounted) return;
        setData(d);
        if (d.confirmed_at) {
          setConfirmed(true);
          setName(d.confirmed_by || "");
        } else {
          setName(d.kontakt_name || "");
        }
      } catch {
        if (mounted) setError("Dieser Bestätigungslink ist ungültig oder abgelaufen.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const confirm = async () => {
    if (name.trim().length < 2) { toast.error("Bitte Ihren Namen angeben"); return; }
    if (!signature) { toast.error("Bitte im Feld unterschreiben"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/einsatzplaene/by-token/${token}/confirm`, {
        name: name.trim(), signature_data_url: signature,
      });
      setConfirmed(true);
      toast.success("Vielen Dank — Einsatzplan bestätigt.");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="plan-error">
          <p className="text-xs uppercase tracking-[0.25em] text-red-700">Fehler</p>
          <h1 className="font-serif text-3xl mt-4">{error}</h1>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-einsatzplan" className="min-h-screen bg-[#FAFAF7] py-12 md:py-20">
      <div className="max-w-2xl mx-auto px-5 md:px-10">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Lukas Stutz — Mietkoch</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">
          Einsatzplan bestätigen
        </h1>
        <p className="text-sm text-[#757067] mt-3">
          Plan-Nr. <b className="text-[#BA6D52]">{data.plan_number}</b>
          {data.month_label ? ` · ${data.month_label}` : ""}
        </p>

        <div className="mt-8 bg-white border border-[#E3DECE] p-6 md:p-8" data-testid="plan-details">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Betrieb</p>
          <p className="font-serif text-2xl mt-2">{data.betrieb}</p>
          {data.ort && <p className="text-sm text-[#757067] mt-1">{data.ort}</p>}

          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] mt-8">
            Geplante Einsatztage ({data.dates?.length || 0})
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8" data-testid="plan-dates">
            {(data.dates || []).map((d, i) => (
              <div key={d.datum} className="flex items-baseline gap-3 py-2 border-b border-[#F0ECE2]">
                <span className="text-xs text-[#BA6D52] w-5 shrink-0">{i + 1}.</span>
                <span className="text-sm">{d.datum_de}</span>
                <span className="text-xs text-[#757067]">{d.weekday}</span>
              </div>
            ))}
          </div>

          {data.notiz && (
            <div className="mt-6">
              <span className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] block mb-1">Anmerkung</span>
              <p className="text-sm italic text-[#2C2A26]">{data.notiz}</p>
            </div>
          )}

          <p className="text-xs text-[#757067] leading-relaxed mt-8 pt-6 border-t border-[#E3DECE]">
            Dieser Plan hält nur die Termine fest und enthält keine Beträge. Abgerechnet wird nach
            den tatsächlich geleisteten Stunden — pro Einsatz wird ein Stundennachweis erstellt und
            von der Küche bestätigt. Die Rechnung folgt anschließend gesammelt.
          </p>
        </div>

        {confirmed ? (
          <div data-testid="plan-confirmed" className="mt-8 bg-emerald-50 border border-emerald-200 p-6 md:p-8 text-center">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <h2 className="font-serif text-2xl mt-4 text-emerald-900">Einsatzplan bestätigt</h2>
            <p className="text-sm text-emerald-800 mt-2">
              Vielen Dank{data.confirmed_by ? `, ${data.confirmed_by}` : ""}! Die Bestätigung wurde mit
              Zeitstempel dokumentiert — Sie bekommen den Plan als PDF per E-Mail.
            </p>
          </div>
        ) : (
          <div className="mt-8 bg-[#FAF5F1] border border-[#BA6D52] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Bestätigung</p>
            <p className="text-sm text-[#2C2A26] mt-3 font-light leading-relaxed">
              Bitte bestätigen Sie, dass Lukas Stutz an den oben genannten Tagen für Sie im Einsatz ist.
            </p>

            <label className="block mt-6 text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Ihr Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vor- und Nachname"
              data-testid="plan-name-input"
              className="mt-2 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-4 py-3 text-sm"
            />

            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Unterschrift</p>
            <div className="mt-2">
              <SignaturePad onChange={setSignature} testId="plan-signature" />
            </div>

            <button
              onClick={confirm}
              disabled={saving}
              data-testid="plan-confirm-btn"
              className="mt-6 w-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-3 transition-colors"
            >
              {saving ? (<><Loader2 size={16} className="animate-spin" /> Wird gespeichert …</>)
                      : (<><CalendarCheck size={16} /> Einsatzplan bestätigen</>)}
            </button>
            <p className="text-xs text-[#757067] italic mt-4">
              Sollte ein Termin nicht passen, melden Sie sich bitte direkt bei Lukas Stutz.
            </p>
          </div>
        )}

        <p className="text-xs text-[#757067] text-center mt-12">
          Lukas Stutz — Mietkoch für Gastronomie &amp; Events
        </p>
      </div>
    </div>
  );
}
